/**
 * Design System for College Comparison Hub
 * Centralized color schemes, utilities, and styling patterns
 */

// School-specific color schemes (based on mockup)
export const SCHOOL_COLORS = {
  indigo: {
    bg: 'bg-indigo-50',
    bgLight: 'bg-indigo-100/10',
    border: 'border-indigo-800',
    borderLight: 'border-indigo-300',
    text: 'text-indigo-900',
    textLight: 'text-indigo-700',
    accent: 'bg-indigo-500/90',
    accentHover: 'hover:bg-indigo-700',
    gradient: 'from-indigo-400 to-indigo-500/90',
    marker: 'text-indigo-900',
    positionBg: 'bg-indigo-500/20'
  },
  teal: {
    bg: 'bg-teal-50',
    bgLight: 'bg-teal-100/10',
    border: 'border-teal-800',
    borderLight: 'border-teal-300',
    text: 'text-teal-900',
    textLight: 'text-teal-700',
    accent: 'bg-teal-500/90',
    accentHover: 'hover:bg-teal-700',
    gradient: 'from-teal-400 to-teal-500/90',
    marker: 'text-teal-900',
    positionBg: 'bg-teal-500/20'
  },
  gold: {
    bg: 'bg-gold-50',
    bgLight: 'bg-gold-100/10',
    border: 'border-gold-800',
    borderLight: 'border-gold-300',
    text: 'text-gold-900',
    textLight: 'text-gold-700',
    accent: 'bg-gold-500/90',
    accentHover: 'hover:bg-gold-700',
    gradient: 'from-gold-400 to-gold-500/90',
    marker: 'text-slate-900',
    positionBg: 'bg-gold-500/20'
  },
  coral: {
    bg: 'bg-coral-50',
    bgLight: 'bg-coral-100/10',
    border: 'border-coral-800',
    borderLight: 'border-coral-300',
    text: 'text-coral-800',
    textLight: 'text-coral-700',
    accent: 'bg-coral-500/90',
    accentHover: 'hover:bg-coral-700',
    gradient: 'from-coral-400 to-coral-500/90',
    marker: 'text-coral-800',
    positionBg: 'bg-coral-500/20'
  },
  slate: {
    bg: 'bg-slate-50',
    bgLight: 'bg-slate-100/10',
    border: 'border-slate-800',
    borderLight: 'border-slate-300',
    text: 'text-slate-900',
    textLight: 'text-slate-700',
    accent: 'bg-slate-500/90',
    accentHover: 'hover:bg-slate-700',
    gradient: 'from-slate-400 to-slate-500/90',
    marker: 'text-slate-900',
    positionBg: 'bg-slate-500/20'
  }
};

// Category-based color schemes (Reach/Target/Safety)
export const CATEGORY_COLORS = {
  reach: {
    bg: 'bg-coral-50',
    border: 'border-coral-600',
    text: 'text-coral-900',
    star: 'text-coral-500',
    label: 'text-coral-700'
  },
  target: {
    bg: 'bg-gold-50',
    border: 'border-gold-600',
    text: 'text-gold-900',
    star: 'text-gold-500',
    label: 'text-gold-700'
  },
  safety: {
    bg: 'bg-teal-50',
    border: 'border-teal-600',
    text: 'text-teal-900',
    star: 'text-teal-500',
    label: 'text-teal-700'
  },
  additional: {
    bg: 'bg-slate-50',
    border: 'border-slate-600',
    text: 'text-slate-900',
    star: 'text-slate-500',
    label: 'text-slate-700'
  }
};

// Assign school colors by index (cycles through colors)
const COLOR_SEQUENCE = ['coral', 'gold', 'teal', 'slate'];

/**
 * Get color scheme for a school by index
 * @param {number} index - School index (0-based)
 * @returns {object} Color scheme object
 */
export const getSchoolColor = (index) => {
  const colorKey = COLOR_SEQUENCE[index % COLOR_SEQUENCE.length];
  return SCHOOL_COLORS[colorKey];
};

/**
 * Get color scheme based on category
 * @param {string} category - College category (reach/target/safety)
 * @returns {object} Color scheme object
 */
export const getCategoryColors = (category) => {
  const normalized = category?.toLowerCase();
  return CATEGORY_COLORS[normalized] || CATEGORY_COLORS.additional;
};

/**
 * Format currency values
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format percentage values
 * @param {number} value - Decimal value (0-1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value) => {
  if (!value && value !== 0) return 'N/A';
  return `${Math.round(value * 100)}%`;
};

/**
 * Format array fields (handles strings, arrays, JSON)
 * @param {*} value - Value to format
 * @param {number} maxItems - Maximum items to display
 * @returns {string} Formatted string
 */
export const formatArrayField = (value, maxItems = 4) => {
  if (!value) return 'Not specified';

  if (Array.isArray(value)) {
    return value.slice(0, maxItems).join(', ');
  }

  if (typeof value === 'string' && value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.slice(0, maxItems).join(', ') : value;
    } catch {
      return value;
    }
  }

  return value;
};

/**
 * Get college name from college object
 * @param {object} college - College object
 * @returns {string} College name
 */
export const getCollegeName = (college) => {
  return college?.global_colleges?.name || college?.name || 'Unknown College';
};

/**
 * Calculate competitive position score (1-5 stars)
 * @param {object} college - College object
 * @param {object} competitivePositions - AI-generated positions
 * @returns {number} Score from 1-5
 */
export const calculatePositionScore = (college, competitivePositions = {}) => {
  const collegeName = getCollegeName(college);

  // Use AI-generated position if available
  if (competitivePositions[collegeName]) {
    return competitivePositions[collegeName].stars || 3;
  }

  // Fallback to basic calculation
  const acceptanceRate = college.global_colleges?.acceptance_rate || 0.5;
  const category = college.category?.toLowerCase();

  if (category === 'reach') {
    return acceptanceRate < 0.1 ? 2 : acceptanceRate < 0.2 ? 2.5 : 3;
  } else if (category === 'target') {
    return acceptanceRate < 0.3 ? 3.5 : 4;
  } else if (category === 'safety') {
    return acceptanceRate < 0.5 ? 4.5 : 5;
  }

  return 3;
};

/**
 * Get position label based on category and score
 * @param {object} college - College object
 * @param {object} competitivePositions - AI-generated positions
 * @returns {string} Position label
 */
export const getPositionLabel = (college, competitivePositions = {}) => {
  const collegeName = getCollegeName(college);

  // Use AI-generated label if available
  if (competitivePositions[collegeName]) {
    return competitivePositions[collegeName].label || 'Under Review';
  }

  // Fallback to basic calculation
  const score = calculatePositionScore(college, competitivePositions);
  const category = college.category?.toLowerCase();

  if (category === 'reach') {
    return score <= 2 ? 'Strong Reach' : score <= 3 ? 'Moderate Reach' : 'Accessible Reach';
  } else if (category === 'target') {
    return score >= 4 ? 'Strong Match' : 'Good Match';
  } else if (category === 'safety') {
    return score >= 4.5 ? 'Very Likely' : 'Likely';
  }

  return 'Under Review';
};

/**
 * Shared button styles
 */
export const BUTTON_STYLES = {
  primary: 'px-4 py-2 bg-teal-500/90 text-white rounded-lg font-medium border-2 border-slate-900 hover:bg-teal-700 transition-all active:scale-95',
  secondary: 'px-4 py-2 bg-white text-slate-700 rounded-lg font-medium border border-stone-300 hover:bg-stone-50 transition-all active:scale-95',
  tab: 'px-5 py-3 rounded-lg font-medium border-2 flex items-center gap-2 whitespace-nowrap transition-all active:scale-98',
  tabActive: 'bg-teal-500/90 text-white border-slate-900',
  tabInactive: 'bg-white text-slate-700 border-stone-300 hover:bg-stone-50'
};

/**
 * Shared card styles
 */
export const CARD_STYLES = {
  base: 'bg-white rounded-lg overflow-hidden transition-all duration-200',
  bordered: 'border-2',
  hover: 'hover:shadow-lg hover:-translate-y-1'
};

/**
 * Shared table styles
 */
export const TABLE_STYLES = {
  container: 'bg-white border-2 border-slate-900 rounded-lg overflow-hidden',
  header: 'bg-stone-100 text-left p-4 font-bold text-slate-900 border-b-2 border-slate-900',
  cell: 'p-4 text-slate-900 bg-white',
  row: 'border-b border-stone-200'
};
